import { useEffect, useCallback, Fragment, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import intl from 'react-intl-universal';
import isEmpty from 'lodash/isEmpty';
import { Typography, Icon } from '@getsynapse/design-system';
import {
  getAllUsers,
  updateUsersPagination,
  selectAvailableLicenses,
  setSearchParam,
  selectUsersPaginationData,
} from 'state/UsersManagement/usersManagementSlice';
import { selectOrganizationLicense } from 'state/Organization/organizationSlice';
import { fetchFilterSettingByType } from 'state/Settings/Filters/FiltersSlice';
import { LICENSE_TIER, SETTINGS_FILTERS } from 'utils/constants';
import { License } from 'utils/customTypes';
import MoreActionsPopup from 'Organisms/TableHeaderActionButtons/MoreActionsPopup';
import useImportLDUsers from './hooks/useImportLDUsers';
import useImportFacilitatorUsers from './hooks/useImportFacilitatorUsers';
import useImportBusinessUsers from './hooks/useImportBusinessUsers';
import UsersTable from './UsersTable';
import Pagination from 'Organisms/Pagination/ClientPagination';
import AddUserModal from './AddUserModal';
import DeleteUserSnackbar from './UserPage/components/DeleteUserSnackbar/DeleteUserSnackbar';

const UsersListPage = () => {
  const dispatch = useDispatch();
  const pagination = useSelector(selectUsersPaginationData);

  const importLDUsersButtonProps = useImportLDUsers();
  const importFacilitatorUsersButtonProps = useImportFacilitatorUsers();
  const importBusinessUsersButtonProps = useImportBusinessUsers();

  useEffect(() => {
    dispatch(getAllUsers());
    dispatch(fetchFilterSettingByType(SETTINGS_FILTERS.USERS_TABLE));
    dispatch(setSearchParam(''));
  }, [dispatch]);

  const fetchUsersWithPagination = useCallback(
    (params) => {
      dispatch(updateUsersPagination(params));
    },
    [dispatch]
  );

  const [showAddUserModal, setShowAddUserModal] = useState(false);

  const licenseData: License = useSelector(selectOrganizationLicense);
  const availableLicenses = useSelector(selectAvailableLicenses);

  return (
    <Fragment>
      <AddUserModal isOpen={showAddUserModal} setIsOpen={setShowAddUserModal} />
      <div className='flex justify-between items-center mb-4'>
        <Typography className='text-neutral-dark' weight='medium'>
          {intl.get('USERS_PAGE.TITLE')}
        </Typography>
        <div className='flex'>
          {licenseData.license_tier !== LICENSE_TIER.TRIAL && (
            <div className='flex flex-row mr-8 items-center'>
              <Icon
                name='information-circle-outline'
                className='text-xl mr-2'
              />
              <Typography
                variant='body2'
                className='text-neutral-dark leading-4'
              >
                {intl.get('LICENSE.LICENSE_REMAINING', {
                  purchasedLicense: licenseData.license_number,
                  licensesLeft: availableLicenses,
                })}
              </Typography>
            </div>
          )}
          <MoreActionsPopup
            options={[
              {
                label: intl.get(
                  'USERS_PAGE.TABLE.IMPORT.IMPORT_LEARNING_USERS'
                ),
                isImportButton: true,
                disabled:
                  isEmpty(importLDUsersButtonProps.currentUser) ||
                  isEmpty(importLDUsersButtonProps.settings),
                importButtonProps: importLDUsersButtonProps,
              },
              {
                label: intl.get(
                  'USERS_PAGE.TABLE.IMPORT.IMPORT_FACILITATOR_USERS'
                ),
                isImportButton: true,
                disabled:
                  isEmpty(importFacilitatorUsersButtonProps.currentUser) ||
                  isEmpty(importFacilitatorUsersButtonProps.settings),
                importButtonProps: importFacilitatorUsersButtonProps,
              },
              importBusinessUsersButtonProps.isDataReady && {
                label: intl.get(
                  'USERS_PAGE.TABLE.IMPORT.IMPORT_BUSINESS_USERS'
                ),
                isImportButton: true,
                disabled:
                  isEmpty(importBusinessUsersButtonProps.currentUser) ||
                  isEmpty(importBusinessUsersButtonProps.settings),
                importButtonProps: importBusinessUsersButtonProps,
              },
            ]}
            hasSplitButton
            splitButtonOnClick={() => setShowAddUserModal(true)}
            buttonText={intl.get('ADD_USER.ADD_USER_BUTTON')}
            buttonTestId='add-user_button'
          />
        </div>
      </div>
      <UsersTable />
      <Pagination
        total={pagination.count}
        onChange={fetchUsersWithPagination}
      />
      <DeleteUserSnackbar />
    </Fragment>
  );
};

export default UsersListPage;
